import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const RoleInput = React.memo(({ type, label, form, updateForm, setError }) => {
  const inputRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [inputError, setInputError] = useState('');

  const handleInputChange = (e) => {
    const value = e.target.value;
    updateForm(type, 'name', value);
    setIsDropdownOpen(true);
    setInputError(''); // Clear error while typing
  };

  const handleSelect = (value) => {
    updateForm(type, 'name', value);
    setIsDropdownOpen(false);
    setInputError('');
  };

  const handleBlur = () => {
    setTimeout(() => { // Delay to allow click in dropdown
      const value = form[type].name;
      if (value && !form[type].allOptions.includes(value)) {
        setInputError(`"${value}" is not a valid ${label.split(' ')[1].toLowerCase()}`);
        setError(true); // Notify parent of error state
      } else {
        setInputError('');
        setError(false);
      }
      setIsDropdownOpen(false);
    }, 100);
  };

  const handleFocus = () => {
    setIsDropdownOpen(true);
  };

  return form[type].enabled ? (
    <div className="relative">
      <label className="block text-gray-700 mb-2">{label}</label>
      <input
        ref={inputRef}
        type="text"
        value={form[type].name}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={`Type or select ${label.split(' ')[1].toLowerCase()}...`}
        className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#424752] ${
          inputError ? 'border-red-500' : ''
        }`}
      />
      {isDropdownOpen && (
        <div
          className="absolute z-10 w-full max-h-40 overflow-y-auto bg-white border rounded-lg shadow-lg mt-1"
          style={{ top: '100%' }}
        >
          {form[type].options.length > 0 ? (
            form[type].options.map((name, i) => (
              <div
                key={i}
                onMouseDown={() => handleSelect(name)} // Use mousedown to capture before blur
                className="p-2 hover:bg-gray-100 cursor-pointer text-gray-700 text-sm"
              >
                {name}
              </div>
            ))
          ) : (
            <div className="p-2 text-gray-500">No matches found</div>
          )}
        </div>
      )}
      {inputError && <p className="text-red-500 text-sm mt-1">{inputError}</p>}
    </div>
  ) : null;
}, (prevProps, nextProps) => {
  return (
    prevProps.type === nextProps.type &&
    prevProps.label === nextProps.label &&
    prevProps.form[prevProps.type].enabled === nextProps.form[nextProps.type].enabled &&
    prevProps.form[prevProps.type].name === nextProps.form[nextProps.type].name &&
    prevProps.form[prevProps.type].options === nextProps.form[nextProps.type].options
  );
});

const AssignDesignation = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedFDNs, setSelectedFDNs] = useState([]);
  const [form, setForm] = useState({
    contractor: { enabled: false, name: '', options: [], allOptions: [] },
    operator: { enabled: false, name: '', options: [], allOptions: [] },
    date: new Date().toISOString().split('T')[0],
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(false); // Track form-wide error state
  const projectId = localStorage.getItem('selectedProject');
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchFDNData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${baseURL}get_fdn_addresses_by_project`, {
          params: { projectId },
        });
        const fdnAddresses = Array.isArray(res.data.fdnAddresses) ? res.data.fdnAddresses : [];
        setData(fdnAddresses);
      } catch (err) {
        console.error('Error fetching FDN data:', err);
        setError('Failed to load FDN data');
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFDNData();
  }, [projectId]);

  const fetchDesignations = async () => {
    try {
      const res = await axios.get(`${baseURL}get_designations`, {
        params: { projectId },
      });
      console.log('Designations Response:', res.data);
      const contractors = res.data.Contractor ? Object.keys(res.data.Contractor) : [];
      const operators = res.data['Scanning Operator'] ? Object.keys(res.data['Scanning Operator']) : [];
      setForm(prev => ({
        ...prev,
        contractor: { ...prev.contractor, allOptions: contractors, options: contractors },
        operator: { ...prev.operator, allOptions: operators, options: operators },
      }));
    } catch (err) {
      console.error('Error fetching designations:', err);
    }
  };

  const handleSearch = (e) => setSearch(e.target.value);

  const toggleAll = (e) => {
    const filtered = filteredData;
    setSelectedFDNs(e.target.checked ? filtered : []);
  };

  const toggleFDN = (fdn) => setSelectedFDNs(prev =>
    prev.includes(fdn) ? prev.filter(x => x !== fdn) : [...prev, fdn]
  );

  const updateForm = (key, field, value) => {
    setForm(prev => {
      let updated = { ...prev };
      if (field === undefined || field === 'selectedType') {
        const selectedType = key;
        updated.selectedType = selectedType;
        ['contractor', 'operator'].forEach(type => {
          updated[type] = {
            ...prev[type],
            enabled: type === selectedType
          };
        });
        if (selectedType && !prev[selectedType].allOptions.length) {
          fetchDesignations();
        }

      } else {
        updated[key] = {
          ...prev[key],
          [field]: value
        };
        if (field === 'name') {
          const filtered = prev[key].allOptions.filter(opt =>
            opt.toLowerCase().includes(value.toLowerCase())
          );
          updated[key].options = filtered.length > 0 ? filtered : prev[key].allOptions;
        }
      }

      return updated;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (formError) {
      alert('Please correct the errors in the form before submitting.');
      return;
    }
    const payload = {
      projectId,
      AssignFDN: {
        selectedFDNs,
        contractorName: form.contractor.enabled ? form.contractor.name : null,
        scanningOperatorName: form.operator.enabled ? form.operator.name : null,
        installationDate: form.date,
      },
    };
    try {
      const res = await axios.post(`${baseURL}assign_fdn`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.status === 200) {
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          setSelectedFDNs([]);
          setForm({
            contractor: { enabled: false, name: '', options: [], allOptions: [] },
            operator: { enabled: false, name: '', options: [], allOptions: [] },
            date: new Date().toISOString().split('T')[0],
          });
        }, 3000);
      }
    } catch (err) {
      console.error('Submit error:', err);
      alert('Submission failed.');
    }
  };

  const filteredData = data.filter(fdn =>
    fdn.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex flex-col items-center">
        <h1 className="text-2xl pb-2 font-bold text-gray-800">View and Assign FDN Address</h1>
      <div className="w-full bg-gray-50 rounded-lg shadow-lg p-4 space-y-6">


        {loading && <p>Loading FDN data...</p>}
        {error && <p className="text-red-500">{error}</p>}

        <input
          type="text"
          value={search}
          onChange={handleSearch}
          placeholder="Search by FDN Address"
          className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#424752]"
        />

        {!loading && !error && (
          <div className="h-64 resize-y overflow-y-auto border rounded-lg">
            <table className="min-w-full bg-white">
              <thead className="bg-[#424752] sticky top-0">
                <tr>
                  <th className="py-2 px-3 text-left">
                    <input
                      type="checkbox"
                      onChange={toggleAll}
                      checked={filteredData.length > 0 && selectedFDNs.length === filteredData.length}
                    />
                  </th>
                  <th className="py-2 px-3 text-left text-white text-sm">FDN Addresses</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((fdn, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="py-1 px-3">
                      <input
                        type="checkbox"
                        checked={selectedFDNs.includes(fdn)}
                        onChange={() => toggleFDN(fdn)}
                      />
                    </td>
                    <td className="py-1 px-3 text-gray-700 text-sm">{fdn}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <form onSubmit={submit} className="space-y-6">
          <div className="flex space-x-6">
            {['contractor', 'operator'].map(type => (
              <label key={type} className="flex items-center">
                <input
                  type="radio"
                  name="userType"
                  checked={form.selectedType === type || (form[type]?.enabled && !form.selectedType)}
                  onChange={() => updateForm(type)} // Pass the selected type
                  value={type}
                  className="h-5 w-5 text-[#424752]"
                />
                <span className="ml-2 text-gray-700">
                  {type === 'contractor' ? 'Contractor' : 'Scanning Operator'}
                </span>
              </label>
            ))}
          </div>

          <div>
            <label className="block text-gray-700 mb-2">Installation Date</label>
            <input
              type="date"
              value={form.date}
              onChange={e => setForm(prev => ({ ...prev, date: e.target.value }))}
              className="w-56 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#424752]"
            />
          </div>

          <RoleInput type="contractor" label="Search Contractor Name" form={form} updateForm={updateForm} setError={setFormError} />
          <RoleInput type="operator" label="Search Scanning Operator Name" form={form} updateForm={updateForm} setError={setFormError} />

          <button
            type="submit"
            disabled={formError}
            className={`w-36 mx-auto block text-white py-3 rounded-lg transition duration-300 ${
              formError ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#424752] hover:bg-gray-700'
            }`}
          >
            Assign
          </button>
        </form>

        {showSuccess && (
          <div className="p-4 bg-green-100 text-green-700 rounded-lg">FDN addresses assigned successfully!</div>
        )}
      </div>
    </div>
  );
};

export default AssignDesignation;